import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Power, Search, AlertTriangle, PackagePlus } from "lucide-react";

const EMPTY = {
  name: "", brand: "", category: "Retail", sku: "", actual_price: 0, price: 0,
  stock_qty: 0, low_stock_threshold: 5, branch_ids: [], branch_prices: {},
  status: "active", incentive_eligible: false,
};

export default function Products() {
  const { branches } = useBranch();
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const isAdmin = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const payload = () => ({
    ...form, actual_price: Number(form.actual_price || 0), price: Number(form.price || 0),
    stock_qty: Number(form.stock_qty || 0), low_stock_threshold: Number(form.low_stock_threshold || 0),
  });

  const save = async () => {
    if (!form.name) { toast.error("Product name is required"); return; }
    try {
      if (editId) await api.put(`/products/${editId}`, payload());
      else await api.post("/products", payload());
      toast.success("Product saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleStatus = async (p) => {
    const next = p.status === "inactive" ? "active" : "inactive";
    try { await api.put(`/products/${p.id}`, { ...EMPTY, ...p, status: next }); toast.success(next === "active" ? "Activated" : "Deactivated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const restock = async (p) => {
    const val = window.prompt(`Add stock for "${p.name}" (current ${p.stock_qty}). Enter quantity to add:`, "10");
    if (val == null) return;
    const delta = parseInt(val, 10);
    if (Number.isNaN(delta) || delta === 0) return;
    try { await api.post(`/products/${p.id}/stock`, { delta, reason: "restock" }); toast.success("Stock updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };

  const filtered = products.filter((p) => {
    const t = q.toLowerCase();
    return !t || (p.name || "").toLowerCase().includes(t) || (p.brand || "").toLowerCase().includes(t) || (p.category || "").toLowerCase().includes(t);
  });
  const lowCount = products.filter((p) => p.low_stock && p.status !== "inactive").length;

  return (
    <div>
      <PageHeader title="Products & Inventory" subtitle="Retail products sold at billing — with low-stock alerts"
        actions={isOwner && <Button onClick={openNew} data-testid="add-product-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Product</Button>} />

      {lowCount > 0 && (
        <div className="mb-4 flex items-center gap-2 text-sm text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-sm px-3 py-2" data-testid="low-stock-banner">
          <AlertTriangle size={16} /> {lowCount} product{lowCount > 1 ? "s are" : " is"} low on stock — restock soon.
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
        <Input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-list-search" className="pl-9 bg-secondary" />
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Product</TableHead><TableHead>Brand</TableHead><TableHead>Category</TableHead>
              <TableHead className="text-right">MRP</TableHead><TableHead className="text-right">Sell</TableHead>
              <TableHead className="text-right">Stock</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} className="border-border" data-testid={`product-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-muted-foreground">{p.brand || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{p.category}</TableCell>
                <TableCell className="text-right text-muted-foreground line-through">{p.actual_price ? rupee(p.actual_price) : "—"}</TableCell>
                <TableCell className="text-right text-primary font-medium">{rupee(p.price)}</TableCell>
                <TableCell className="text-right">
                  <span className={p.low_stock ? "text-orange-300 font-medium" : ""} data-testid={`product-stock-${p.id}`}>{p.stock_qty}</span>
                  {p.low_stock && <Badge variant="outline" className="ml-2 text-orange-300 border-orange-500/40 text-[10px]">LOW</Badge>}
                </TableCell>
                <TableCell><Badge variant="outline" className={p.status === "inactive" ? "text-muted-foreground" : "text-green-300 border-green-800"}>{p.status === "inactive" ? "Inactive" : "Active"}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => restock(p)} data-testid={`restock-${p.id}`} title="Add stock"><PackagePlus size={14} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil size={14} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className={`h-8 w-8 ${p.status === "inactive" ? "text-green-400" : "text-orange-300"}`} onClick={() => toggleStatus(p)} data-testid={`toggle-product-${p.id}`}><Power size={14} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No products.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Product Name *</Label><Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Brand</Label><Input data-testid="product-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Category</Label><Input data-testid="product-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">MRP / Actual (₹)</Label><Input type="number" data-testid="product-actual-price" value={form.actual_price} onChange={(e) => setForm({ ...form, actual_price: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Selling Price (₹) *</Label><Input type="number" data-testid="product-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Stock Quantity</Label><Input type="number" data-testid="product-stock" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Low-Stock Alert Below</Label><Input type="number" data-testid="product-threshold" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="bg-secondary mt-1" /></div>
            </div>
            <div><Label className="text-xs">SKU (optional)</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="bg-secondary mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="product-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save Product</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
